import { DatabaseIcon, DocumentTextIcon, MailIcon, PencilAltIcon, UploadIcon, XIcon } from "@heroicons/react/outline";
import { useCallback, useContext, useEffect, useState } from "react";
import { useModal, WorkflowContext } from "../contexts";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import WorkflowDocumentOutputName from "./WorkflowDocumentOutptName";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import WorkflowSelectModal from "./WorkflowSelectModal";
import { Document, Region, SignatureField } from "../interfaces";
import useUser from "../hooks/useUser";
import PayWallModalPro from "./PayWallModalPro";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import FileUploadModal from "./FileUploadModal";
import AdobePDFViewer from "./AdobePDFViewer";
import GooglePickerButton from "./GooglePickerButton";
import OAuthModal from "./OAuthModal";
import ErrorModal from "./ErrorModal";
import { getData, OAuthError, postData, putData } from "../backend";
import { BLOCK_ICON_MAP } from "../utils";
import { useTranslation } from "react-i18next";
import LoadingModal from "./LoadingModal";
import WorkflowSignatureConfig from "./WorkflowSignatureConfig";
import PDFViewer from "./PDFViewer";

function WorkflowDocumentPDF() {
  const { user, hasPermission } = useUser();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const index = (() => {
    const hash = location.hash.split("#")[1];

    if (hash === "signable")
      return workflow.documents.indexOf(workflow.documents.filter(d => d.isSignable)[0]);

    return (parseInt(hash) || 1) - 1;
  })();

  const initialDocument = workflow.documents[index] ?? workflow.documents[0];
  const [document, setDocument] = useState<Document>();

  const [hasChanges, setHasChanges] = useState(false);

  const containsFields = !!document?.config["containsFields"];

  const [currentPage, setCurrentPage] = useState(1);
  const [currentScale, setCurrentScale] = useState(1);

  const [regions, setRegions] = useState<Array<Region>>([]);
  const [enableEditMode, setEnableEditMode] = useState(false);

  useEffect(() => {
    if (!document || pdfUrl)
      return;

    postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`)
      .then(({ url }) => setPdfUrl(url));
  }, [document, pdfUrl]);

  useEffect(() => {
    // Check if the document was created before the "next step message" was introduced
    if (document || !initialDocument)
      return;

    setDocument(initialDocument);
    setRegions(initialDocument.fieldRegions || []);
    setEnableEditMode(!!initialDocument.config["containsFields"] && !initialDocument.isSignable)

  }, [document, initialDocument]);

  const uploadPDfFile = useCallback(async (content: Blob, filename?: string, isNewFile?: boolean) => {
    if (isNewFile)
      setPdfUrl(null);

    return postData(`/workflows/documents/${document!.id}/generate-pdf-upload-url/`)
      .then(({ url }) => {
        return fetch(url, {
          method: "PUT",
          headers: {
            "content-type": "application/pdf"
          },
          body: content
        })
      })
      .then(() => postData(`/workflows/documents/${document!.id}/confirm-pdf-upload-status/`, { filename }))
      .then(() => postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`))
      .then(({ url }) => setPdfUrl(url))
      .then(() => setDocument({ ...document!, file: { ...document!.file!, name: filename! } }))
      .then(() => getData(`/workflows/documents/${document!.id}/`))
      .then(setDocument)
      .catch(console.error);
  }, [document]);

  function handleInsertSignatureField(type: SignatureField, recipient: number) {
    setHasChanges(true);

    const id = String(new Date().getTime());
    setRegions(prev => [...prev, { rect: { x: 100, y: 400, width: type === "CHECKBOX" ? 40 : 200, height: 40, scale: currentScale }, id, type, recipient, pageNumber: currentPage }])

    setEnableEditMode(false);
  }

  async function handleSaveChanges() {
    if (!document)
      return;

    const updatedDocument = { ...document, fieldRegions: regions };
    setDocument(updatedDocument);

    await putData(`/workflows/documents/${document!.id}/`, updatedDocument)
      .then(setDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);

    setHasChanges(false);
  }

  function handleUploadFile(file: File): Promise<any> {
    closeModal();

    const content = new Blob([file], { type: "application/pdf" });
    return uploadPDfFile(content, file.name, true);
  }

  async function updateDocument(field: string, value: any) {
    if (!document)
      return;

    const updatedDocument = { ...document, [field]: value };
    setDocument(updatedDocument);

    await putData(`/workflows/documents/${document!.id}/`, updatedDocument)
      .then(setDocument)
      .then(() => mutateWorkflow())
      .catch(console.error);
  }

  async function handleChangeFile(file: any) {
    setPdfUrl(null); // NOTE: This will keep the viewer as "Loading..." if an error occurs from selecting the file.

    try {
      await putData(`/workflows/documents/${document!.id}/file/`, { file })
        .then(() => postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`))
        .then(({ url }) => setPdfUrl(url))
        .then(() => setDocument({ ...document!, file: { ...document!.file!, name: file.name } }))
    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />)
      else
        openModal(<ErrorModal details={error} />)
    }

    await mutateWorkflow();
  }

  function toggleRemoveBranding(flag: boolean) {
    if (!user?.subscription?.featureRemoveBranding) {
      openModal(<PayWallModalPro />)
      return;
    }

    updateDocument("removeBranding", flag);
  }

  async function addSignatureLinkSource() {
    if (!workflow || !mutateWorkflow)
      return;

    openModal(<LoadingModal title={t("Adding new Block")} subtitle={t("Please wait")} />)

    const sourceType = "SIGNATURE_LINK";
    try {
      const sourceOptions = { sourceType };

      await postData(`/workflows/${workflow!.id}/source/`, sourceOptions);
      await mutateWorkflow();

      closeModal();

      //@ts-ignore
      const dataLayer = window.dataLayer;
      dataLayer.push({ "event": "add_source", 'source_type': sourceType });

    } catch (error) {
      if (error instanceof OAuthError)
        openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);
    }
  }

  if (!document) {
    if (!location.pathname.endsWith("/document"))
      return null;

    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full w-full">
        <div className="font-semibold font-gilroy text-gray-600 text-center w-96">
          {t("This workflow contains no document templates, would you like to add some?")}
        </div>
        <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Add Workflow Template")} subtitle={t("Add a template to your workflow")} showDocuments workflowContext={workflowContext} />)}>
          {t("Add Document Template")}
        </button>
      </div>
    );
  }

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/document")}>
      {hasPermission(workflow.team, "workflows.edit") &&
        <TransitionWorkflowPageSideBar>
          {!!workflow.source &&
            <>
              <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} document={document} onCopy={() => setEnableEditMode(true)} />
              <WorkflowDocumentOutputName initialValue={document.outputName} onEnter={(value) => updateDocument("outputName", value)} />

              <div className="flex justify-start items-center font-gilroy font-semibold text-sm  w-full">
                <ToggleButton value={!!user?.subscription?.featureRemoveBranding && document.removeBranding} onChange={toggleRemoveBranding} />
                <div className={`font-semibold text-sm ml-2 mr-1 ${true ? "text-gray-600" : "text-gray-400"}`}>
                  {t("Remove Portant PDF branding")}
                </div>
                <ProLabel />
              </div>

              <hr />

              <WorkflowSignatureConfig document={document} updateDocument={updateDocument} insertSignatureField={handleInsertSignatureField} />

              {workflow.documents.length === 0 && <>
                <hr />
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center font-gilroy font-semibold text-gray-600">
                      <DocumentTextIcon className="w-6 h-6 mr-1" /> {t("Add another document")}
                    </div>
                    <div className="font-semibold text-xs text-gray-400">
                      {t("Create multiple documents the same workflow")}
                    </div>
                  </div>
                  <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add document")} subtitle={t("Add another document to this workflow")} showDocuments workflowContext={workflowContext} />)}>
                    {t("Add")}
                  </button>
                </div>
              </>}

              {workflow.emails.length === 0 && <>
                <hr />
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center font-gilroy font-semibold text-gray-600">
                      <MailIcon className="w-6 h-6 mr-1" /> {t("Share document via Gmail")}
                    </div>
                    <div className="font-semibold text-xs text-gray-400">
                      {t("Automatically share created documents")}
                    </div>
                  </div>
                  <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add sharing block")} subtitle={t("Automatically share created documents")} showEmails workflowContext={workflowContext} />)}>
                    {t("Add")}
                  </button>
                </div>
              </>}

            </>}

          {/* TODO: When signable PDFs are public */}
          {(!workflow.source) && <>
            <div className="flex flex-col gap-4 justify-center h-full">
              <div className="flex flex-col gap-2 font-semibold text-gray-600">
                <div className="flex items-center">
                  <DatabaseIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Connect a Source")}
                  </div>
                </div>
                {t("Automatically create documents from data in HubSpot, Google Sheets and more")}
                <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Source")} subtitle={t("Create or select a source where your data will come from")} showSources workflowContext={workflowContext} />)}>
                  {t("Connect")}
                </button>
              </div>
            </div>
          </>}

          {/* TODO: Update by removing "false" when signable PDFs are public */}
          {(!workflow.source && false) &&
            <div className="flex flex-col gap-4 justify-center h-full">
              {containsFields &&
                <div className="flex flex-col gap-2 font-semibold text-gray-600">
                  <div className="flex items-center">
                    <DatabaseIcon className="w-6 h-6 mr-2" />
                    <div className="font-gilroy font-semibold text-lg">
                      {t("Connect a Source")}
                    </div>
                  </div>
                  {t("Automatically create documents from data in HubSpot, Google Sheets and more")}
                  <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Source")} subtitle={t("Create or select a source where your data will come from")} showSources workflowContext={workflowContext} />)}>
                    {t("Connect")}
                  </button>
                  <div className="flex items-center gap-2 my-4">
                    <div className="h-px bg-gray-600 w-full" />
                    <div className="flex-shrink-0">or</div>
                    <div className="h-px bg-gray-600 w-full" />
                  </div>
                  <div className="flex items-center">
                    <PencilAltIcon className="w-6 h-6 mr-2" />
                    <div className="font-gilroy font-semibold text-lg">
                      {t("Create a Signature Link")}
                    </div>
                  </div>
                  {t("Add signable fields to your document and generate a link for signing")}
                  <button className="btn btn-purple" onClick={addSignatureLinkSource}>
                    {t("Configure")}
                  </button>
                </div>
              }
              {!containsFields &&
                <div className="flex flex-col gap-2 font-semibold text-gray-600">
                  <div className="flex items-center">
                    <PencilAltIcon className="w-6 h-6 mr-2" />
                    <div className="font-gilroy font-semibold text-lg">
                      {t("Requests Signatures")}
                    </div>
                  </div>
                  {t("Add signable fields to your document to have your document signed")}
                  <button className="btn btn-blue" onClick={addSignatureLinkSource}>
                    {t("Configure")}
                  </button>
                  <div className="flex items-center gap-2 my-4">
                    <div className="h-px bg-gray-600 w-full" />
                    <div className="flex-shrink-0">or</div>
                    <div className="h-px bg-gray-600 w-full" />
                  </div>
                  <div className="flex items-center">
                    <DatabaseIcon className="w-6 h-6 mr-2" />
                    <div className="font-gilroy font-semibold text-lg">
                      {t("Connect a Source")}
                    </div>
                  </div>
                  {t("Automatically create documents from data in HubSpot, Google Sheets and more")}
                  <button className="btn btn-white" onClick={() => openModal(<WorkflowSelectModal title={t("Select Source")} subtitle={t("Create or select a source where your data will come from")} showSources workflowContext={workflowContext} />)}>
                    {t("Connect")}
                  </button>
                </div>
              }
            </div>
          }
        </TransitionWorkflowPageSideBar>
      }

      <TransitionWorkflowPageContent>
        <div className="flex w-full items-center pb-1 border-b">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md flex-shrink-0" src={BLOCK_ICON_MAP[document.documentType]} alt="Google File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center w-full">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(document.file?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {document.file?.name}
                </div>
              </div>
              {hasPermission(workflow.team, "workflows.edit") &&
                <div className="flex gap-2 w-full">
                  <GooglePickerButton className="flex items-center text-gray-400 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 group" blockType={"PDF"} onPicked={handleChangeFile}>
                    <img className="h-4 mr-1 grayscale group-hover:grayscale-0" src={BLOCK_ICON_MAP["GOOGLE_DRIVE"]} alt="Google Drive" /><>{t("Select from Google Drive")}</>
                  </GooglePickerButton>
                  <button className="flex items-center text-gray-400 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 group" onClick={() => openModal(<FileUploadModal onUpload={handleUploadFile} allowedFormats={['PDF']} />)}>
                    <UploadIcon className="h-4 mr-1 group-hover:text-blue" /><>Upload from device</>
                  </button>
                  {(containsFields && document.isSignable) &&
                    <button className="ml-12 flex items-center text-gray-400 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 disabled:hover:no-underline disabled:hover:text-gray-400 disabled:cursor-not-allowed" onClick={() => setEnableEditMode(f => !f)} disabled={hasChanges}>
                      {enableEditMode
                        ? <><DocumentTextIcon className="h-4 mr-1 group-hover:text-blue" />{t("Edit eSignature fields")}</>
                        : <><PencilAltIcon className="h-4 mr-1 group-hover:text-blue" />{t("Edit PDF form fields")}</>
                      }
                    </button>
                  }
                </div>
              }
            </div>
            {hasChanges &&
              <button className="mx-auto mr-4 flex items-center bg-blue text-white px-4 py-2 rounded text-sm font-gilroy font-semibold flex-shrink-0" onClick={handleSaveChanges}>
                {t("Save Changes")}
              </button>
            }
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer flex-shrink-0" onClick={() => history.push(url)} />
          </div>
        </div>
        {pdfUrl
          ? (
            enableEditMode
              ? <AdobePDFViewer className="w-full h-[800px]" src={pdfUrl} filename={document.file?.name ?? t("PDF File")} saveCallback={uploadPDfFile} />
              : <PDFViewer pdfUrl={pdfUrl} setCurrentPage={setCurrentPage} setCurrentScale={setCurrentScale} regions={regions} updateRegions={(regions) => { setRegions(regions); setHasChanges(true); }} />
          )
          : <div className="w-full h-[800px] flex items-center justify-center text-gray-600">{t("Loading...")}</div>
        }
      </TransitionWorkflowPageContent>
    </TransitionWorkflowPage>
  );

}

export default WorkflowDocumentPDF;
